import type { AccountFeaturesGetResponse } from '@syf-api-library/account-features'
import type {
  AccountSummaryGetResponse,
  ProductType
} from '@syf-api-library/account-summary'
import type { Account } from '@syf-api-library/credit-accounts'
import {
  ACCOUNT_AUTH_PROHIBITED,
  ACCOUNT_CLOSED,
  ACCOUNT_CLOSED_WITH_BALANCE,
  ACCOUNT_CREDIT_LINE_INCREASE_ELIGIBLE,
  ACCOUNT_DELINQUENT,
  ACCOUNT_DORMANT,
  ACCOUNT_OPEN,
  ACCOUNT_PRE_DELIVERY
} from 'const/account/conditions'
import { CARECREDIT } from 'const/account/clients'
import { PSCC, RC } from 'const/account/products'
import PRODUCT_TYPE from 'const/productTypes'
import getPaymentButtonType from './getPaymentButtonType'
import isAccountTypeSecuredInstallment from './isAccountTypeSecuredInstallment'
import isAccountTypeSetpayOrPaylater from './isAccountTypeSetpayOrPaylater'

export interface AccountContext {
  account: Account
  summary: AccountSummaryGetResponse
  features: AccountFeaturesGetResponse | null
}

export interface AccountAggregate {
  careCreditAccountsCount: number
}

/**
 * Flat, boolean-first view of a single account. Rules read this instead of
 * raw API shapes so an API rename only touches this normalizer.
 */
export interface FeatureContext {
  productType: string | undefined
  clientId: string | undefined

  isRevolving: boolean
  isCardProduct: boolean
  isCareCredit: boolean
  isRegistered: boolean
  isSecuredInstallment: boolean
  isSetpayOrPaylater: boolean
  isSettlementLoan: boolean
  isAccountOpen: boolean
  isClosed: boolean
  isClosedWithBalance: boolean
  isDelinquent: boolean
  isDormant: boolean
  isPreDelivery: boolean
  isServiceable: boolean
  inCapOrSettlement: boolean
  isCardOverlaid: boolean
  showsRegisterButton: boolean

  currentBalance: number

  hasAccessToRewards: boolean
  hasAccessToRewardsMfe: boolean
  hasAccessToBalanceTransfers: boolean
  hasAccessToChangeDueDate: boolean
  hasAccessToCreditBalanceRefund: boolean
  hasAccessToCreditView: boolean
  hasAccessToCreditLimitIncrease: boolean
  hasAccessToPaymentSecurity: boolean
  hasAccessToPromotions: boolean
  hasAccessToStatements: boolean
  hasAccessToManageAuthUser: boolean
  hasAccountServicingEnabled: boolean
  hasCashAdvancePinEnabled: boolean
  isCreditLineIncreaseEligible: boolean

  shouldShowMakeAPayment: boolean
  shouldShowCreditBalanceRefund: boolean
  shouldShowCollectionPayment: boolean

  hasSingleCareCreditAccount: boolean
}

type AccountRecord = AccountContext['account']
type AccountSummary = AccountContext['summary']
type AccountFeatures = AccountContext['features']

const CARD_PRODUCT_TYPES: ProductType[] = [
  PRODUCT_TYPE.DUAL_CARD,
  PRODUCT_TYPE.LIMITED_USE_DUAL_CARD,
  PRODUCT_TYPE.BUSINESS_RETAIL_CARD,
  PRODUCT_TYPE.PRIVATE_LABEL
]

const OVERLAY_PRODUCT_TYPES: ProductType[] = [
  ...CARD_PRODUCT_TYPES,
  PRODUCT_TYPE.INSTALLMENT_LOAN,
  PRODUCT_TYPE.INSTALLMENT_LOAN_MONTHLY
]

const PAY_LATER_PRODUCT_TYPES: ProductType[] = [
  PRODUCT_TYPE.INSTALLMENT_LOAN_MONTHLY,
  PRODUCT_TYPE.INSTALLMENT_LOAN_PAY_IN_FOUR
]

const getProductIdentity = (
  account: AccountRecord,
  summary: AccountSummary
) => {
  const productType = summary.product?.type
  return {
    productType,
    clientId: summary.product?.clientId,
    isRevolving: [RC, PSCC].includes(summary.product?.lineOfBusiness as string),
    isCardProduct: CARD_PRODUCT_TYPES.includes(productType),
    isCareCredit: account.product?.portfolio?.toLowerCase() === CARECREDIT,
    isRegistered: Boolean(account.registration?.registered),
    isSecuredInstallment: isAccountTypeSecuredInstallment(account.product.type),
    isSetpayOrPaylater: isAccountTypeSetpayOrPaylater(productType),
    isSettlementLoan:
      productType === PRODUCT_TYPE.INSTALLMENT_LOAN_MONTHLY &&
      summary.billing?.settlementEnrolled === true
  }
}

const getCardSurfaces = (account: AccountRecord, summary: AccountSummary) => {
  const productType = summary.product?.type
  const isRegistered = Boolean(account.registration?.registered)
  const { lineOfBusiness, clientId } = summary.product ?? {}

  return {
    isCardOverlaid:
      !isRegistered &&
      (summary.conditions ?? []).includes(ACCOUNT_AUTH_PROHIBITED) &&
      OVERLAY_PRODUCT_TYPES.includes(productType),
    showsRegisterButton:
      !isRegistered &&
      !PAY_LATER_PRODUCT_TYPES.includes(productType) &&
      Boolean(lineOfBusiness && clientId)
  }
}

const getAccountStatus = (summary: AccountSummary) => {
  const conditions = summary.conditions ?? []
  return {
    isAccountOpen: conditions.includes(ACCOUNT_OPEN),
    isClosed: conditions.includes(ACCOUNT_CLOSED),
    isClosedWithBalance: conditions.includes(ACCOUNT_CLOSED_WITH_BALANCE),
    isDelinquent: conditions.includes(ACCOUNT_DELINQUENT),
    isDormant: conditions.includes(ACCOUNT_DORMANT),
    isPreDelivery: conditions.includes(ACCOUNT_PRE_DELIVERY),
    isCreditLineIncreaseEligible: conditions.includes(
      ACCOUNT_CREDIT_LINE_INCREASE_ELIGIBLE
    ),
    isServiceable: Boolean(summary.servicing?.allowable),
    inCapOrSettlement: Boolean(
      summary.billing?.customerAssistancePlan ||
        summary.billing?.settlementEnrolled
    ),
    currentBalance: Number(summary.credit?.currentBalance)
  }
}

const getEntitlements = (features: AccountFeatures) => ({
  hasAccessToRewards: Boolean(features?.rewardDetails?.accessToRewards),
  hasAccessToRewardsMfe: Boolean(features?.rewardDetails?.accessToRewardsMfe),
  hasAccessToBalanceTransfers: Boolean(features?.accessToBalanceTransfers),
  hasAccessToChangeDueDate: Boolean(features?.accessToChangeDueDate),
  hasAccessToCreditBalanceRefund: Boolean(
    features?.accessToCreditBalanceRefund
  ),
  hasAccessToCreditView: Boolean(features?.accessToCreditView),
  hasAccessToCreditLimitIncrease: Boolean(
    features?.accessToCreditLimitIncrease
  ),
  hasAccessToPaymentSecurity: Boolean(features?.accessToPaymentSecurity),
  hasAccessToPromotions: Boolean(features?.accessToPromotions),
  hasAccessToStatements: Boolean(features?.accessToStatements),
  hasAccessToManageAuthUser: Boolean(features?.accessToManageAuthUser),
  hasAccountServicingEnabled: Boolean(features?.accountServicingEnabled),
  hasCashAdvancePinEnabled: Boolean(features?.cashAdvancePinEnabled)
})

const getPaymentButtons = (ctx: AccountContext) => {
  const payment = getPaymentButtonType({
    account: ctx.account,
    accountSummary: ctx.summary,
    accessToCreditBalanceRefund: Boolean(
      ctx.features?.accessToCreditBalanceRefund
    )
  })
  return {
    shouldShowMakeAPayment: payment.shouldShowMakeAPayment,
    shouldShowCreditBalanceRefund: payment.shouldShowCreditBalanceRefund,
    shouldShowCollectionPayment: payment.shouldShowCollectionPayment
  }
}

const normalizeFeatureContext = (
  ctx: AccountContext,
  aggregate?: AccountAggregate
): FeatureContext => ({
  ...getProductIdentity(ctx.account, ctx.summary),
  ...getCardSurfaces(ctx.account, ctx.summary),
  ...getAccountStatus(ctx.summary),
  ...getEntitlements(ctx.features),
  ...getPaymentButtons(ctx),
  hasSingleCareCreditAccount: aggregate?.careCreditAccountsCount === 1
})

export default normalizeFeatureContext
